import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Copy,
  Film,
  GripVertical,
  Lightbulb,
  Link,
  Loader2,
  Pencil,
  Search,
  Sparkles,
  ThumbsUp,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HookDetailDialog } from "./HookDetailDialog";
import { Id } from "~/convex/_generated/dataModel";
import { Textarea } from "@/components/ui/textarea";
import { VideoUrlDialog } from "./VideoUrlDialog";
import { api } from "~/convex/_generated/api";
import { useMutation } from "convex/react";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";

interface Hook {
  id: string;
  text: string;
  category: string;
  reasoning: string;
  potentialPaths?: string[];
  topic?: string;
  isLiked?: boolean;
  sources?: Array<{
    id: number;
    title: string;
    url: string;
    snippet: string;
  }>;
}

interface SearchResults {
  queries: string[];
  summary: string;
  sources: Array<{
    id: number;
    title: string;
    url: string;
    snippet: string;
  }>;
}

interface ContentHooksProps {
  displayedHooks: Hook[];
  likedHooks: Set<string>;
  filmedHooks?: Set<string>;
  postedHooks?: Set<string>;
  previouslyLikedHooks?: Array<{
    _id: string;
    hookText: string;
    hookCategory: string;
    hookReasoning: string;
    originalDirection?: string;
    likedAt: number;
    isFilmed?: boolean;
    isPosted?: boolean;
    videoUrl?: string;
  }>;
  customPrompt: string;
  isGeneratingHooks: boolean;
  isGeneratingWithSearch: boolean;
  isSearchEnabled: boolean;
  searchResults: SearchResults | null;
  creatorProfile: {
    _id: Id<"exp__creatorProfile">;
    topContentTopics?: string[];
    contentNiche?: string[];
  } | null;
  onCustomPromptChange: (value: string) => void;
  onSearchEnabledChange: (enabled: boolean) => void;
  onGenerateHooks: (direction?: string) => void;
  onLikeHook: (hook: Hook) => void;
  onUnlikeFromLikedList: (hookText: string) => void;
  onMarkAsFilmed: (hookText: string) => void;
  onMarkAsPosted?: (hookText: string) => void;
  onUnmarkAsFilmed?: (hookText: string) => void;
  onUnmarkAsPosted?: (hookText: string) => void;
  onCopyToClipboard: (text: string) => void;
  onRemoveHook: (hookId: string) => void;
  onUpdateDisplayedHook?: (
    hookId: string,
    originalText: string,
    newText: string,
  ) => void;
  onMoveHookToLiked?: (hook: Hook) => void;
  onMoveHookToFilmed?: (hookText: string) => void;
  onMoveHookToPosted?: (hookText: string) => void;
  onMoveFilmedToLiked?: (hookText: string) => void;
  onMovePostedToFilmed?: (hookText: string) => void;
}

export const ContentHooks = ({
  displayedHooks,
  likedHooks,
  filmedHooks = new Set(),
  postedHooks = new Set(),
  previouslyLikedHooks,
  customPrompt: _customPrompt,
  isGeneratingHooks,
  isGeneratingWithSearch,
  isSearchEnabled,
  searchResults,
  creatorProfile,
  onCustomPromptChange,
  onSearchEnabledChange,
  onGenerateHooks,
  onLikeHook,
  onUnlikeFromLikedList,
  onUnmarkAsFilmed,
  onUnmarkAsPosted,
  onCopyToClipboard,
  onRemoveHook,
  onUpdateDisplayedHook,
  onMoveHookToLiked,
  onMoveHookToFilmed,
  onMoveHookToPosted,
  onMoveFilmedToLiked,
  onMovePostedToFilmed,
}: ContentHooksProps) => {
  const [selectedHook, setSelectedHook] = useState<Hook | null>(null);
  const [isHookDialogOpen, setIsHookDialogOpen] = useState(false);
  const [isDirectionDialogOpen, setIsDirectionDialogOpen] = useState(false);
  const [tempDirection, setTempDirection] = useState("");
  const [selectedLikedHook, setSelectedLikedHook] = useState<{
    _id: string;
    hookText: string;
    hookCategory: string;
    hookReasoning: string;
    originalDirection?: string;
    likedAt: number;
    isFilmed?: boolean;
    isPosted?: boolean;
    scriptContent?: string;
    videoUrl?: string;
  } | null>(null);
  const [hookDialogStage, setHookDialogStage] = useState<
    "generate" | "liked" | "filmed" | "posted"
  >("generate");
  const [isVideoUrlDialogOpen, setIsVideoUrlDialogOpen] = useState(false);
  const [hookToFilm, setHookToFilm] = useState<{
    _id: string;
    hookText: string;
    hookCategory: string;
    hookReasoning: string;
    originalDirection?: string;
    likedAt: number;
    isFilmed?: boolean;
    isPosted?: boolean;
    scriptContent?: string;
    videoUrl?: string;
  } | null>(null);

  const { toast } = useToast();
  const addVideoUrlToHook = useMutation(
    api.exp__creatorInsights.exp__addVideoUrlToHook,
  );
  const [draggedItem, setDraggedItem] = useState<{
    type: "generated" | "liked" | "filmed" | "posted";
    data:
      | Hook
      | {
          _id: string;
          hookText: string;
          hookCategory: string;
          hookReasoning: string;
          originalDirection?: string;
          likedAt: number;
          isFilmed?: boolean;
          isPosted?: boolean;
        };
  } | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  // State for generated hook variations - persists across dialog sessions
  const [generatedHooksPerOriginal, setGeneratedHooksPerOriginal] = useState<
    Record<
      string,
      {
        hooks: Hook[];
        currentVariationType:
          | "variations"
          | "same_topic"
          | "same_structure"
          | null;
        guidance: string;
      }
    >
  >({});

  // Helper functions for managing generated hooks
  const getGeneratedHooksForOriginal = (originalHookText: string) => {
    return (
      generatedHooksPerOriginal[originalHookText] || {
        hooks: [],
        currentVariationType: null,
        guidance: "",
      }
    );
  };

  const updateGeneratedHooksForOriginal = (
    originalHookText: string,
    updater: (current: {
      hooks: Hook[];
      currentVariationType:
        | "variations"
        | "same_topic"
        | "same_structure"
        | null;
      guidance: string;
    }) => {
      hooks: Hook[];
      currentVariationType:
        | "variations"
        | "same_topic"
        | "same_structure"
        | null;
      guidance: string;
    },
  ) => {
    setGeneratedHooksPerOriginal((prev) => ({
      ...prev,
      [originalHookText]: updater(
        getGeneratedHooksForOriginal(originalHookText),
      ),
    }));
  };

  const clearGeneratedHooksForOriginal = (originalHookText: string) => {
    setGeneratedHooksPerOriginal((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [originalHookText]: _, ...newState } = prev;
      return newState;
    });
  };

  // Drag handlers
  const handleDragStart = (
    e: React.DragEvent,
    type: "generated" | "liked" | "filmed" | "posted",
    data:
      | Hook
      | {
          _id: string;
          hookText: string;
          hookCategory: string;
          hookReasoning: string;
          originalDirection?: string;
          likedAt: number;
          isFilmed?: boolean;
          isPosted?: boolean;
        },
  ) => {
    setDraggedItem({ type, data });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, targetColumn: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(targetColumn);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Only clear drag over if we're leaving the column entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = (
    e: React.DragEvent,
    targetColumn: "generated" | "liked" | "filmed" | "posted",
  ) => {
    e.preventDefault();
    setDragOverColumn(null);
    if (!draggedItem) return;

    const { type: sourceColumn, data } = draggedItem;

    // Don't do anything if dropping in the same column
    if (sourceColumn === targetColumn) {
      setDraggedItem(null);
      return;
    }

    // Handle different drop scenarios
    if (sourceColumn === "generated" && targetColumn === "liked") {
      // Generated -> Liked
      if (onMoveHookToLiked && "id" in data) {
        onMoveHookToLiked(data as Hook);
      }
    } else if (sourceColumn === "liked" && targetColumn === "filmed") {
      // Liked -> Filmed - directly move (no URL required anymore)
      if (onMoveHookToFilmed && "hookText" in data) {
        onMoveHookToFilmed(data.hookText);
      }
    } else if (sourceColumn === "liked" && targetColumn === "posted") {
      // Liked -> Posted - require video URL (same as filmed -> posted)
      if ("hookText" in data) {
        setHookToFilm(data);
        setIsVideoUrlDialogOpen(true);
      }
    } else if (sourceColumn === "filmed" && targetColumn === "posted") {
      // Filmed -> Posted - require video URL
      if ("hookText" in data) {
        setHookToFilm(data);
        setIsVideoUrlDialogOpen(true);
      }
    } else if (sourceColumn === "filmed" && targetColumn === "liked") {
      // Filmed -> Liked
      if (onMoveFilmedToLiked && "hookText" in data) {
        onMoveFilmedToLiked(data.hookText);
      }
    } else if (sourceColumn === "posted" && targetColumn === "filmed") {
      // Posted -> Filmed
      if (onMovePostedToFilmed && "hookText" in data) {
        onMovePostedToFilmed(data.hookText);
      }
    }

    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverColumn(null);
  };

  // Helper function to determine if a drop is valid
  const isValidDrop = (sourceColumn: string, targetColumn: string) => {
    if (sourceColumn === targetColumn) return false;

    // Define valid transitions
    const validTransitions: Record<string, string[]> = {
      generated: ["liked"],
      liked: ["filmed", "posted"], // Allow direct liked → posted
      filmed: ["posted", "liked"],
      posted: ["filmed"],
    };

    return validTransitions[sourceColumn]?.includes(targetColumn) || false;
  };

  // Helper function to get drop zone message
  const getDropZoneMessage = (sourceColumn: string, targetColumn: string) => {
    if (!isValidDrop(sourceColumn, targetColumn)) return null;

    const messages: Record<string, Record<string, string>> = {
      generated: {
        liked: "Drop to like this hook",
      },
      liked: {
        filmed: "Drop to mark as filmed",
        posted: "Drop to mark as posted",
      },
      filmed: {
        posted: "Drop to mark as posted",
        liked: "Drop to move back to liked",
      },
      posted: {
        filmed: "Drop to move back to filmed",
      },
    };

    return messages[sourceColumn]?.[targetColumn] || null;
  };

  const handleOpenHookDialog = (
    hook:
      | Hook
      | {
          _id: string;
          hookText: string;
          hookCategory: string;
          hookReasoning: string;
          originalDirection?: string;
          likedAt: number;
          isFilmed?: boolean;
          isPosted?: boolean;
          scriptContent?: string;
          videoUrl?: string;
        },
  ) => {
    // Convert liked hook format to Hook format if needed
    if ("_id" in hook) {
      // Determine stage based on hook state
      if (hook.isPosted || (hook.isFilmed && hook.videoUrl)) {
        setHookDialogStage("posted");
      } else if (hook.isFilmed) {
        setHookDialogStage("filmed");
      } else {
        setHookDialogStage("liked");
      }

      setSelectedLikedHook(hook);
      setSelectedHook({
        id: hook._id,
        text: hook.hookText,
        category: hook.hookCategory,
        reasoning: hook.hookReasoning,
      });
    } else {
      setHookDialogStage("generate");
      setSelectedHook(hook);
      setSelectedLikedHook(null);
    }
    setIsHookDialogOpen(true);
  };

  // Handle video URL submission for moving to posted
  const handleVideoUrlSubmit = async (videoUrl: string) => {
    if (!hookToFilm || !creatorProfile) return;

    try {
      await addVideoUrlToHook({
        creatorId: creatorProfile._id,
        hookText: hookToFilm.hookText,
        videoUrl: videoUrl,
      });

      if (onMoveHookToPosted) {
        onMoveHookToPosted(hookToFilm.hookText);
      }

      toast({
        title: "Hook Moved to Posted!",
        description: "Video URL added and hook marked as posted.",
      });

      setIsVideoUrlDialogOpen(false);
      setHookToFilm(null);
    } catch (error) {
      console.error("Error adding video URL:", error);
      toast({
        title: "Failed to Add Video",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle marking hook as posted with video URL requirement
  const handleMarkAsPostedClick = (hook: {
    _id: string;
    hookText: string;
    hookCategory: string;
    hookReasoning: string;
    originalDirection?: string;
    likedAt: number;
    isFilmed?: boolean;
    isPosted?: boolean;
    scriptContent?: string;
    videoUrl?: string;
  }) => {
    setHookToFilm(hook);
    setIsVideoUrlDialogOpen(true);
  };

  // Separate liked hooks into different categories
  const likedButNotFilmedHooks =
    previouslyLikedHooks?.filter(
      (hook) =>
        !filmedHooks.has(hook.hookText) && !postedHooks.has(hook.hookText),
    ) || [];
  const filmedButNotPostedHooks =
    previouslyLikedHooks?.filter(
      (hook) =>
        filmedHooks.has(hook.hookText) && !postedHooks.has(hook.hookText),
    ) || [];
  const postedHooksList =
    previouslyLikedHooks?.filter((hook) => postedHooks.has(hook.hookText)) ||
    [];

  const renderHookCard = (hook: Hook, index: number, showNumber = true) => (
    <div
      key={hook.id}
      className="group border rounded-lg pt-4 pb-8 px-4 cursor-pointer bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 relative"
      onClick={() => handleOpenHookDialog(hook)}
      draggable
      onDragStart={(e) => handleDragStart(e, "generated", hook)}
      onDragEnd={handleDragEnd}
    >
      {/* Drag handle */}
      <div
        className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>

      {/* Top icons */}
      <div
        className="absolute top-2 right-2 flex gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onLikeHook(hook);
          }}
          className="text-blue-600 hover:text-blue-700 hover:border-blue-300 p-2"
        >
          <ThumbsUp className="w-3 h-3" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onRemoveHook(hook.id);
          }}
          className="text-red-600 hover:text-red-700 hover:border-red-300 p-2"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>

      {/* Bottom left variations button (visible on hover) */}
      <div
        className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenHookDialog(hook);
          }}
          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 h-auto"
        >
          Click to explore variations
        </Button>
      </div>

      {/* Bottom right copy icon */}
      <div
        className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onCopyToClipboard(hook.text);
          }}
          className="p-2"
        >
          <Copy className="w-3 h-3" />
        </Button>
      </div>

      <div className="pr-4 pl-4">
        {showNumber && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              #{index + 1}
            </span>
          </div>
        )}
        <p className="font-medium text-gray-900 dark:text-gray-100 text-base leading-relaxed mb-2">
          {hook.text}
        </p>
        {hook.topic && (
          <Badge variant="secondary" className="text-xs">
            {hook.topic}
          </Badge>
        )}
      </div>
    </div>
  );

  const renderLikedHookCard = (
    likedHook: {
      _id: string;
      hookText: string;
      hookCategory: string;
      hookReasoning: string;
      hookTopic?: string;
      originalDirection?: string;
      likedAt: number;
      isFilmed?: boolean;
    },
    _index: number,
  ) => (
    <div
      key={likedHook._id}
      className="border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20 rounded-lg p-4 cursor-pointer relative group"
      onClick={() => handleOpenHookDialog(likedHook)}
      draggable
      onDragStart={(e) => handleDragStart(e, "liked", likedHook)}
      onDragEnd={handleDragEnd}
    >
      {/* Drag handle */}
      <div
        className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>

      {/* Top icons */}
      <div
        className="absolute top-2 right-2 flex gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onMoveHookToFilmed && onMoveHookToFilmed(likedHook.hookText)
          }
          className="text-purple-600 hover:text-purple-700 hover:border-purple-300 p-2"
        >
          <Film className="w-3 h-3" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onUnlikeFromLikedList(likedHook.hookText)}
          className="text-red-600 hover:text-red-700 hover:border-red-300 p-2"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>

      {/* Bottom right copy icon */}
      <div
        className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => onCopyToClipboard(likedHook.hookText)}
          className="p-2"
        >
          <Copy className="w-3 h-3" />
        </Button>
      </div>

      <div className="pr-4">
        <p className="font-medium text-gray-900 dark:text-gray-100 text-base leading-relaxed mb-2">
          {likedHook.hookText}
        </p>
        {likedHook.hookTopic && (
          <Badge variant="secondary" className="text-xs mb-2">
            {likedHook.hookTopic}
          </Badge>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-500">
          Liked on {new Date(likedHook.likedAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  );

  const renderFilmedHookCard = (
    filmedHook: {
      _id: string;
      hookText: string;
      hookCategory: string;
      hookReasoning: string;
      originalDirection?: string;
      likedAt: number;
      isFilmed?: boolean;
      isPosted?: boolean;
    },
    _index: number,
  ) => (
    <div
      key={filmedHook._id}
      className="border border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/20 rounded-lg p-4 cursor-pointer relative group"
      onClick={() => handleOpenHookDialog(filmedHook)}
      draggable
      onDragStart={(e) => handleDragStart(e, "filmed", filmedHook)}
      onDragEnd={handleDragEnd}
    >
      {/* Drag handle */}
      <div
        className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>

      {/* Top icons */}
      <div
        className="absolute top-2 right-2 flex gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleMarkAsPostedClick(filmedHook)}
          className="text-green-600 hover:text-green-700 hover:border-green-300 p-2"
        >
          <Link className="w-3 h-3" />
        </Button>
        {onUnmarkAsFilmed && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onUnmarkAsFilmed(filmedHook.hookText)}
            className="text-blue-600 hover:text-blue-700 hover:border-blue-300 p-2"
          >
            <ThumbsUp className="w-3 h-3" />
          </Button>
        )}
      </div>

      {/* Bottom right copy icon */}
      <div
        className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => onCopyToClipboard(filmedHook.hookText)}
          className="p-2"
        >
          <Copy className="w-3 h-3" />
        </Button>
      </div>

      <div className="pr-4">
        <p className="font-medium text-gray-900 dark:text-gray-100 text-base leading-relaxed mb-2">
          {filmedHook.hookText}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-500">
          Filmed on {new Date(filmedHook.likedAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  );

  const renderPostedHookCard = (
    postedHook: {
      _id: string;
      hookText: string;
      hookCategory: string;
      hookReasoning: string;
      originalDirection?: string;
      likedAt: number;
      isFilmed?: boolean;
      isPosted?: boolean;
      videoUrl?: string;
    },
    _index: number,
  ) => (
    <div
      key={postedHook._id}
      className="border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20 rounded-lg p-4 cursor-pointer relative group"
      onClick={() => handleOpenHookDialog(postedHook)}
      draggable
      onDragStart={(e) => handleDragStart(e, "posted", postedHook)}
      onDragEnd={handleDragEnd}
    >
      {/* Drag handle */}
      <div
        className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>

      {/* Top right unmark icon (if available) */}
      {onUnmarkAsPosted && (
        <div
          className="absolute top-2 right-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => onUnmarkAsPosted(postedHook.hookText)}
            className="text-purple-600 hover:text-purple-700 hover:border-purple-300 p-2"
          >
            <Film className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* Bottom right copy icon */}
      <div
        className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => onCopyToClipboard(postedHook.hookText)}
          className="p-2"
        >
          <Copy className="w-3 h-3" />
        </Button>
      </div>

      <div className="pr-4">
        <p className="font-medium text-gray-900 dark:text-gray-100 text-base leading-relaxed mb-2">
          {postedHook.hookText}
        </p>
        <div className="space-y-1">
          {postedHook.videoUrl && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-green-600 dark:text-green-400">
                Video:
              </span>
              <a
                href={postedHook.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 underline"
                onClick={(e) => e.stopPropagation()}
              >
                {postedHook.videoUrl.length > 30
                  ? `${postedHook.videoUrl.substring(0, 30)}...`
                  : postedHook.videoUrl}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              Content Ideas
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                <ThumbsUp className="w-4 h-4" />
                <span>{likedButNotFilmedHooks.length} liked</span>
              </div>
              <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
                <Film className="w-4 h-4" />
                <span>{filmedButNotPostedHooks.length} filmed</span>
              </div>
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <Link className="w-4 h-4" />
                <span>{postedHooksList.length} posted</span>
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search Results Display */}
          {searchResults && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Search className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                    Search-Powered Generation
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {searchResults.summary}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Kanban Board */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Column 1: Generate */}
            <div
              className={`space-y-4 border-2 rounded-lg p-4 transition-all duration-200 ${
                dragOverColumn === "generated" &&
                draggedItem &&
                isValidDrop(draggedItem.type, "generated")
                  ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20"
                  : "border-gray-200 dark:border-gray-700"
              }`}
              onDragOver={(e) => handleDragOver(e, "generated")}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, "generated")}
            >
              {/* Drop zone indicator */}
              {dragOverColumn === "generated" &&
                draggedItem &&
                isValidDrop(draggedItem.type, "generated") && (
                  <div className="absolute inset-0 flex items-center justify-center bg-amber-100 dark:bg-amber-900/30 border-2 border-dashed border-amber-400 rounded-lg z-10 pointer-events-none">
                    <div className="text-amber-700 dark:text-amber-300 font-medium text-center">
                      <Sparkles className="w-8 h-8 mx-auto mb-2" />
                      {getDropZoneMessage(draggedItem.type, "generated")}
                    </div>
                  </div>
                )}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  Generate
                </h3>

                {!isGeneratingHooks && !isGeneratingWithSearch && (
                  <div className="flex items-center gap-2">
                    <Dialog
                      open={isDirectionDialogOpen}
                      onOpenChange={setIsDirectionDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={
                            isGeneratingHooks ||
                            isGeneratingWithSearch ||
                            !creatorProfile
                          }
                          className="p-2"
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[425px]">
                        <DialogHeader>
                          <DialogTitle>Custom Direction</DialogTitle>
                          <DialogDescription>
                            Add specific direction or topic for your hooks. This
                            will help AI generate more targeted content.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">
                              Direction (Optional)
                            </label>
                            <Textarea
                              placeholder="e.g., 'Focus on productivity tips' or 'Create hooks about personal growth'"
                              value={tempDirection}
                              onChange={(e) => setTempDirection(e.target.value)}
                              className="resize-none"
                              rows={3}
                            />
                          </div>

                          {/* Search Toggle */}
                          {tempDirection.trim() && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                              <div className="flex items-center gap-3">
                                <Search
                                  className={`w-4 h-4 ${isSearchEnabled ? "text-blue-600 dark:text-blue-400" : "text-gray-400"}`}
                                />
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    Search the web
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Get real-time information for more accurate
                                    hooks
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() =>
                                  onSearchEnabledChange(!isSearchEnabled)
                                }
                                className={`relative w-11 h-6 rounded-full ${
                                  isSearchEnabled
                                    ? "bg-blue-600"
                                    : "bg-gray-300 dark:bg-gray-600"
                                }`}
                              >
                                <div
                                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                                    isSearchEnabled
                                      ? "translate-x-5"
                                      : "translate-x-0"
                                  }`}
                                />
                              </button>
                            </div>
                          )}
                        </div>
                        <DialogFooter>
                          <Button
                            onClick={() => {
                              onCustomPromptChange(tempDirection);
                              onGenerateHooks(tempDirection || undefined);
                              setIsDirectionDialogOpen(false);
                            }}
                            disabled={
                              isGeneratingHooks || isGeneratingWithSearch
                            }
                            className={
                              tempDirection.trim() && isSearchEnabled
                                ? "bg-blue-600 hover:bg-blue-700 text-white"
                                : ""
                            }
                          >
                            {isGeneratingHooks || isGeneratingWithSearch ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {isGeneratingWithSearch && tempDirection.trim()
                                  ? "Searching & Generating..."
                                  : "Generating..."}
                              </>
                            ) : (
                              <>
                                {isSearchEnabled && tempDirection.trim() ? (
                                  <Search className="w-4 h-4 mr-2" />
                                ) : (
                                  <Sparkles className="w-4 h-4 mr-2" />
                                )}
                                {isSearchEnabled && tempDirection.trim()
                                  ? "Generate with Search"
                                  : "Generate Hooks"}
                              </>
                            )}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
                {(isGeneratingHooks || isGeneratingWithSearch) && (
                  <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span className="text-xs">generating...</span>
                  </div>
                )}
              </div>

              {/* Topic/Niche Quick Filters */}
              {creatorProfile &&
                (creatorProfile.topContentTopics ||
                  creatorProfile.contentNiche) && (
                  <div className="space-y-2 relative bottom-2">
                    <div className="flex flex-wrap gap-2">
                      {(
                        creatorProfile.topContentTopics ||
                        creatorProfile.contentNiche ||
                        []
                      ).map((topic, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors text-xs"
                          onClick={() => {
                            const direction = `Create hooks focused on ${topic}`;
                            onCustomPromptChange(direction);
                            onGenerateHooks(direction);
                          }}
                        >
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              <div className="space-y-3 min-h-[400px] max-h-[600px] overflow-y-auto scrollbar-hide">
                {displayedHooks.map((hook, index) =>
                  renderHookCard(hook, index),
                )}
                {displayedHooks.length === 0 &&
                  !isGeneratingHooks &&
                  !isGeneratingWithSearch && (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-sm">Generate hooks to get started</p>
                    </div>
                  )}
                {displayedHooks.length < 5 &&
                  displayedHooks.length > 0 &&
                  (isGeneratingHooks || isGeneratingWithSearch) && (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Loading more hooks...</span>
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* Column 2: Liked */}
            <div
              className={`space-y-4 border-2 rounded-lg p-4 transition-all duration-200 relative ${
                dragOverColumn === "liked" &&
                draggedItem &&
                isValidDrop(draggedItem.type, "liked")
                  ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                  : "border-gray-200 dark:border-gray-700"
              }`}
              onDragOver={(e) => handleDragOver(e, "liked")}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, "liked")}
            >
              {/* Drop zone indicator */}
              {dragOverColumn === "liked" &&
                draggedItem &&
                isValidDrop(draggedItem.type, "liked") && (
                  <div className="absolute inset-0 flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 border-2 border-dashed border-blue-400 rounded-lg z-10 pointer-events-none">
                    <div className="text-blue-700 dark:text-blue-300 font-medium text-center">
                      <ThumbsUp className="w-8 h-8 mx-auto mb-2" />
                      {getDropZoneMessage(draggedItem.type, "liked")}
                    </div>
                  </div>
                )}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <ThumbsUp className="w-5 h-5 text-blue-600" />
                    Liked ({likedButNotFilmedHooks.length})
                  </h3>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-2">
                  💡 The more hooks you like, the better we'll become at
                  generating hooks tailored to your style!
                </div>
              </div>
              <div className="space-y-3 min-h-[400px] max-h-[600px] overflow-y-auto scrollbar-hide">
                {likedButNotFilmedHooks.map((likedHook, index) =>
                  renderLikedHookCard(likedHook, index),
                )}
                {likedButNotFilmedHooks.length === 0 && (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <ThumbsUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-sm">Like hooks to save them here</p>
                  </div>
                )}
              </div>
            </div>

            {/* Column 3: Filmed */}
            <div
              className={`space-y-4 border-2 rounded-lg p-4 transition-all duration-200 relative ${
                dragOverColumn === "filmed" &&
                draggedItem &&
                isValidDrop(draggedItem.type, "filmed")
                  ? "border-purple-400 bg-purple-50 dark:bg-purple-900/20"
                  : "border-gray-200 dark:border-gray-700"
              }`}
              onDragOver={(e) => handleDragOver(e, "filmed")}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, "filmed")}
            >
              {/* Drop zone indicator */}
              {dragOverColumn === "filmed" &&
                draggedItem &&
                isValidDrop(draggedItem.type, "filmed") && (
                  <div className="absolute inset-0 flex items-center justify-center bg-purple-100 dark:bg-purple-900/30 border-2 border-dashed border-purple-400 rounded-lg z-10 pointer-events-none">
                    <div className="text-purple-700 dark:text-purple-300 font-medium text-center">
                      <Film className="w-8 h-8 mx-auto mb-2" />
                      {getDropZoneMessage(draggedItem.type, "filmed")}
                    </div>
                  </div>
                )}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Film className="w-5 h-5 text-purple-600" />
                  Filmed ({filmedButNotPostedHooks.length})
                </h3>
              </div>
              <div className="space-y-3 min-h-[400px] max-h-[600px] overflow-y-auto scrollbar-hide">
                {filmedButNotPostedHooks.map((filmedHook, index) =>
                  renderFilmedHookCard(filmedHook, index),
                )}
                {filmedButNotPostedHooks.length === 0 && (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <Film className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-sm">Move liked hooks to filmed</p>
                  </div>
                )}
              </div>
            </div>

            {/* Column 4: Posted */}
            <div
              className={`space-y-4 border-2 rounded-lg p-4 transition-all duration-200 relative ${
                dragOverColumn === "posted" &&
                draggedItem &&
                isValidDrop(draggedItem.type, "posted")
                  ? "border-green-400 bg-green-50 dark:bg-green-900/20"
                  : "border-gray-200 dark:border-gray-700"
              }`}
              onDragOver={(e) => handleDragOver(e, "posted")}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, "posted")}
            >
              {/* Drop zone indicator */}
              {dragOverColumn === "posted" &&
                draggedItem &&
                isValidDrop(draggedItem.type, "posted") && (
                  <div className="absolute inset-0 flex items-center justify-center bg-green-100 dark:bg-green-900/30 border-2 border-dashed border-green-400 rounded-lg z-10 pointer-events-none">
                    <div className="text-green-700 dark:text-green-300 font-medium text-center">
                      <Link className="w-8 h-8 mx-auto mb-2" />
                      {getDropZoneMessage(draggedItem.type, "posted")}
                    </div>
                  </div>
                )}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Link className="w-5 h-5 text-green-600 " />
                  Posted ({postedHooksList.length})
                </h3>
              </div>
              <div className="space-y-3 min-h-[400px] max-h-[600px] overflow-y-auto scrollbar-hide">
                {postedHooksList.map((postedHook, index) =>
                  renderPostedHookCard(postedHook, index),
                )}
                {postedHooksList.length === 0 && (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <Film className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-sm">Add video URL to filmed hooks</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hook Detail Dialog */}
      <HookDetailDialog
        open={isHookDialogOpen}
        onOpenChange={setIsHookDialogOpen}
        hook={selectedHook}
        creatorProfileId={creatorProfile?._id || null}
        onLikeHook={onLikeHook}
        onCopyHook={onCopyToClipboard}
        likedHooks={likedHooks}
        generatedHooksData={
          selectedHook && hookDialogStage === "generate"
            ? getGeneratedHooksForOriginal(selectedHook.text)
            : { hooks: [], currentVariationType: null, guidance: "" }
        }
        onGeneratedHooksUpdate={(updater) => {
          if (selectedHook && hookDialogStage === "generate") {
            updateGeneratedHooksForOriginal(selectedHook.text, updater);
          }
        }}
        onClearGeneratedHooks={() => {
          if (selectedHook && hookDialogStage === "generate") {
            clearGeneratedHooksForOriginal(selectedHook.text);
          }
        }}
        onUpdateHook={(originalText, newText) => {
          // Update the selected hook if it's the one being edited
          if (selectedHook && selectedHook.text === originalText) {
            setSelectedHook({ ...selectedHook, text: newText });
          }
          // Update the displayedHooks array in the parent component
          if (onUpdateDisplayedHook && selectedHook) {
            onUpdateDisplayedHook(selectedHook.id, originalText, newText);
          }
        }}
        stage={hookDialogStage}
        likedHookData={selectedLikedHook}
        onMarkAsFilmed={(hookText) => {
          // Handle marking as filmed from within the dialog (if needed)
          if (onMoveHookToFilmed) {
            onMoveHookToFilmed(hookText);
          }
        }}
        onMarkAsPosted={(hookText) => {
          // Handle marking as posted from within the dialog
          if (onMoveHookToPosted) {
            onMoveHookToPosted(hookText);
          }
        }}
      />

      {/* Video URL Dialog */}
      <VideoUrlDialog
        open={isVideoUrlDialogOpen}
        onOpenChange={setIsVideoUrlDialogOpen}
        onSubmit={handleVideoUrlSubmit}
      />
    </div>
  );
};
